// manual-e2e-test-assistant のシナリオ型定義（src/shared/types.ts と互換）
// createdAt / updatedAt は拡張機能のインポート時に自動補完されるため含めない

import type { FunctionDeclaration } from './llm.js'

export interface Scenario {
  id: string
  name: string
  description?: string
  version: string
  targetUrl?: string
  categories: Category[]
}

export interface Category {
  id: string
  name: string
  description?: string
  steps: TestStep[]
}

export interface TestStep {
  id: string
  title: string
  instruction: string
  expected: string
  tips?: string
  url?: string
  tags?: string[]
  dependsOn?: string[]
  inputData?: Record<string, unknown>
}

// Gemini の function calling は自由キーのオブジェクト（propertiesなしのobject）を
// 受け付けないため、inputData はツール入力上 key/value の配列として受け取り、
// normalizeToolInput() でシナリオ形式の Record に変換する
const stepSchema = {
  type: 'object',
  properties: {
    id: {
      type: 'string',
      description:
        'ステップID。シナリオ全体で一意。カテゴリIDを接頭辞にした連番形式（例: "login-001"）',
    },
    title: { type: 'string', description: 'ステップの短いタイトル' },
    instruction: {
      type: 'string',
      description: 'テスターが実行する操作の説明。具体的で再現可能な手順にする',
    },
    expected: { type: 'string', description: '期待される結果。確認可能な観点で書く' },
    tips: { type: 'string', description: '補足情報・ヒント（任意）' },
    url: { type: 'string', description: 'このステップで開くURL（任意）' },
    tags: {
      type: 'array',
      items: { type: 'string' },
      description: 'タグ（任意）例: ["smoke", "regression"]',
    },
    dependsOn: {
      type: 'array',
      items: { type: 'string' },
      description: '依存する他ステップのIDリスト（任意）。循環依存は不可',
    },
    inputData: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          key: { type: 'string', description: '入力項目名（例: "email"）' },
          value: { type: 'string', description: '入力値（例: "test@example.com"）' },
        },
        required: ['key', 'value'],
      },
      description:
        'テストで使う入力データのリスト（任意）例: [{"key": "email", "value": "test@example.com"}]',
    },
  },
  required: ['id', 'title', 'instruction', 'expected'],
}

const categorySchema = {
  type: 'object',
  properties: {
    id: {
      type: 'string',
      description: 'カテゴリID。シナリオ全体で一意。ケバブケース（例: "login"）',
    },
    name: { type: 'string', description: 'カテゴリ名（例: "ログイン機能"）' },
    description: { type: 'string', description: 'カテゴリの説明（任意）' },
    steps: { type: 'array', items: stepSchema, description: 'テストステップの配列' },
  },
  required: ['id', 'name', 'steps'],
}

export const scenarioInputSchema = {
  type: 'object',
  properties: {
    id: {
      type: 'string',
      description: 'シナリオID。ケバブケース（例: "user-login-test"）',
    },
    name: { type: 'string', description: 'シナリオ名' },
    description: { type: 'string', description: 'シナリオの説明（任意）' },
    version: { type: 'string', description: 'セマンティックバージョン（例: "1.0.0"）' },
    targetUrl: { type: 'string', description: 'テスト対象のURL（任意）' },
    categories: {
      type: 'array',
      items: categorySchema,
      description: 'テストカテゴリの配列',
    },
  },
  required: ['id', 'name', 'version', 'categories'],
}

export const UPDATE_SCENARIO_TOOL_NAME = 'update_scenario'

export const updateScenarioFunctionDeclaration: FunctionDeclaration = {
  name: UPDATE_SCENARIO_TOOL_NAME,
  description:
    'テストシナリオ全体を作成・更新する。部分更新ではなく、常にシナリオの完全な内容を渡すこと。' +
    '既存のシナリオを編集する場合も、変更のない部分を含めた完成形を渡す（渡された内容でそのまま置き換えられる）。' +
    '既存のカテゴリIDやステップIDは、ユーザーの指示がない限り変更しない。',
  parameters: scenarioInputSchema,
}

/**
 * ツール入力をシナリオ形式に正規化する。
 * inputData が [{key, value}] 配列で来た場合は Record に変換する
 * （モデルがオブジェクト形式で送ってきた場合はそのまま維持）。
 */
export function normalizeToolInput(input: unknown): Scenario {
  const scenario = input as Scenario
  if (!Array.isArray(scenario?.categories)) return scenario

  for (const category of scenario.categories) {
    if (!Array.isArray(category?.steps)) continue
    for (const step of category.steps) {
      const raw = step.inputData as unknown
      if (Array.isArray(raw)) {
        const record: Record<string, unknown> = {}
        for (const entry of raw) {
          if (entry && typeof entry === 'object' && 'key' in entry) {
            const { key, value } = entry as { key: unknown; value?: unknown }
            if (typeof key === 'string' && key) record[key] = value
          }
        }
        if (Object.keys(record).length > 0) {
          step.inputData = record
        } else {
          delete step.inputData
        }
      }
    }
  }
  return scenario
}

export function countSteps(scenario: Scenario): number {
  return scenario.categories.reduce((sum, cat) => sum + cat.steps.length, 0)
}
